import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess } from "@/lib/roles";
import { Plus, Truck, PackageCheck, PackageX, AlertTriangle, Search, Package, CheckCircle2 } from "lucide-react";

const SHIPMENT_TYPE_LABEL = { pallets: "Pallets", boxes: "Boxes", pallets_and_boxes: "Pallets + Boxes" };

const STATUS_META = {
  draft:                 { cls: "bg-slate-500/10 text-slate-300 border-slate-400/40", label: "Draft" },
  counting:              { cls: "bg-blue-500/10 text-blue-300 border-blue-400/40", label: "Counting" },
  review:                { cls: "bg-purple-500/10 text-purple-300 border-purple-400/40", label: "Review" },
  accepted:              { cls: "bg-emerald-500/10 text-emerald-300 border-emerald-400/40", label: "Accepted" },
  accepted_with_issues:  { cls: "bg-[color:var(--warning)]/10 text-[color:var(--warning)] border-[color:var(--warning)]/40", label: "Accepted With Issues" },
  cancelled:             { cls: "bg-red-500/10 text-red-300 border-red-400/40", label: "Cancelled" },
};

function humanTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); }
  catch { return iso; }
}

export default function ReceivingList() {
  const { user } = useAuth();
  const nav = useNavigate();
  const canReceive = hasAccess(user?.role, ["admin", "gm", "manager"]);
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [supplierId, setSupplierId] = useState("");
  const [shipmentType, setShipmentType] = useState("all");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (status !== "all") params.set("status_", status);
    if (shipmentType !== "all") params.set("shipment_type", shipmentType);
    if (supplierId) params.set("supplier_id", supplierId);
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    api.get(`/inventory_receivings?${params.toString()}`).then(({ data }) => setItems(data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/inventory_dashboard").then(({ data }) => setStats(data)).catch(() => {});
  }, [q, status, shipmentType, supplierId, start, end]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/suppliers").then(({ data }) => setSuppliers(data)).catch(() => {});
  }, []);

  const startNew = async () => {
    if (creating) return;
    setCreating(true);
    try {
      const { data } = await api.post("/inventory_receivings", {});
      nav(`/receiving/${data.id}/edit`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setCreating(false); }
  };

  const attention = stats?.needs_attention || [];

  return (
    <div className="space-y-6" data-testid="receiving-list-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="label-caps">Warehouse</div>
          <h1 className="font-heading text-3xl sm:text-4xl">Inventory Receivables</h1>
          <div className="text-[color:var(--text-secondary)] text-sm mt-1">Shipment receiving &amp; pallet acceptance</div>
        </div>
        {canReceive && (
          <button onClick={startNew} disabled={creating}
                  className="hos-btn-primary flex items-center gap-2 disabled:opacity-50"
                  data-testid="receive-shipment-btn">
            <Truck size={16}/> {creating ? "Creating…" : "Receive New Shipment"}
          </button>
        )}
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={<PackageCheck size={16}/>} label="Today's Receipts" value={stats.today_receipts} testid="stat-receipts"/>
          <StatCard icon={<Package size={16}/>} label="Pallets Today" value={stats.today_pallets}/>
          <StatCard icon={<Package size={16}/>} label="Boxes Today" value={stats.today_boxes}/>
          <StatCard icon={<AlertTriangle size={16}/>} label="With Issues" value={stats.today_with_issues} accent={stats.today_with_issues ? "warning" : undefined}/>
        </div>
      )}

      {attention.length > 0 && (
        <div className="hos-card p-4 border border-[color:var(--warning)]/40">
          <div className="flex items-center gap-2 mb-3 text-[color:var(--warning)]">
            <AlertTriangle size={16}/> <span className="label-caps">Needs Attention</span>
          </div>
          <ul className="space-y-2">
            {attention.map((r) => (
              <li key={r.id}>
                <Link to={`/receiving/${r.id}`} className="flex items-center justify-between gap-3 p-3 rounded-md border border-[color:var(--border)] hover:border-[color:var(--gold)]" data-testid={`attention-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold">{r.reference_number} · {r.supplier_name || "—"}</div>
                    <div className="text-xs text-[color:var(--text-secondary)]">{r.total_pallets || 0} pallets · {r.total_boxes || 0} boxes · signed {humanTime(r.signed_at)}</div>
                  </div>
                  <span className={`text-[10px] px-2 py-1 rounded-full border ${STATUS_META[r.status]?.cls}`}>{STATUS_META[r.status]?.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filters */}
      <div className="hos-card p-4 grid grid-cols-1 md:grid-cols-7 gap-2 items-end">
        <div className="md:col-span-2 relative">
          <Search size={14} className="absolute left-3 top-3 text-[color:var(--text-secondary)]"/>
          <input className="hos-input pl-9" placeholder="Search ref, PO, invoice, supplier, driver…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"/>
        </div>
        <select className="hos-input" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="status-filter">
          <option value="all">All statuses</option>
          {Object.entries(STATUS_META).map(([k, m]) => <option key={k} value={k}>{m.label}</option>)}
        </select>
        <select className="hos-input" value={shipmentType} onChange={(e) => setShipmentType(e.target.value)} data-testid="type-filter">
          <option value="all">All types</option>
          <option value="pallets">Pallets</option>
          <option value="boxes">Boxes</option>
          <option value="pallets_and_boxes">Pallets + Boxes</option>
        </select>
        <select className="hos-input" value={supplierId} onChange={(e) => setSupplierId(e.target.value)} data-testid="supplier-filter">
          <option value="">All suppliers</option>
          {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <input type="date" className="hos-input" value={start} onChange={(e) => setStart(e.target.value)} data-testid="start-date"/>
        <input type="date" className="hos-input" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="end-date"/>
      </div>

      {/* History list */}
      <div className="hos-card overflow-hidden">
        <div className="p-4 border-b border-[color:var(--border)] font-heading text-xl">Receiving History</div>
        {items.length === 0 && (
          <div className="p-10 text-center text-[color:var(--text-secondary)]">No receivings yet. Click <b>Receive New Shipment</b> to start.</div>
        )}
        {items.length > 0 && (
          <>
            <div className="hidden md:block">
              <table className="w-full text-sm">
                <thead className="text-left label-caps"><tr>
                  <th className="p-3">Reference</th><th>Supplier</th><th>Type</th><th>Arrival</th>
                  <th>Pallets</th><th>Boxes</th><th>Issues</th><th>Status</th><th></th>
                </tr></thead>
                <tbody>
                  {items.map((r) => (
                    <tr key={r.id} className="border-t border-[color:var(--border)] hover:bg-[color:var(--bg-surface-2)]/40" data-testid={`row-${r.reference_number}`}>
                      <td className="p-3 font-semibold">{r.reference_number}</td>
                      <td>{r.supplier_name || "—"}</td>
                      <td className="text-xs">{SHIPMENT_TYPE_LABEL[r.shipment_type] || "Pallets"}</td>
                      <td className="text-[color:var(--text-secondary)]">{humanTime(r.arrival_at)}</td>
                      <td>{r.total_pallets || 0}</td>
                      <td>{r.total_boxes || 0}</td>
                      <td>{(r.issues || []).length ? <span className="text-[color:var(--warning)]">{r.issues.length}</span> : <span className="text-[color:var(--text-secondary)]">0</span>}</td>
                      <td><span className={`text-[10px] px-2 py-1 rounded-full border ${STATUS_META[r.status]?.cls}`}>{STATUS_META[r.status]?.label || r.status}</span></td>
                      <td className="text-right pr-3">
                        {["accepted", "accepted_with_issues", "cancelled"].includes(r.status)
                          ? <Link to={`/receiving/${r.id}`} className="text-[color:var(--gold)] hover:underline">View</Link>
                          : <Link to={`/receiving/${r.id}/edit`} className="text-[color:var(--gold)] hover:underline">Continue</Link>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="md:hidden divide-y divide-[color:var(--border)]">
              {items.map((r) => (
                <Link key={r.id} to={["accepted", "accepted_with_issues", "cancelled"].includes(r.status) ? `/receiving/${r.id}` : `/receiving/${r.id}/edit`}
                      className="block p-4" data-testid={`card-${r.reference_number}`}>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{r.reference_number}</div>
                    <span className={`text-[10px] px-2 py-1 rounded-full border ${STATUS_META[r.status]?.cls}`}>{STATUS_META[r.status]?.label}</span>
                  </div>
                  <div className="text-sm mt-1">{r.supplier_name || "—"}</div>
                  <div className="text-xs text-[color:var(--text-secondary)] mt-1">{SHIPMENT_TYPE_LABEL[r.shipment_type] || "Pallets"} · {humanTime(r.arrival_at)} · {r.total_pallets || 0} pallets · {r.total_boxes || 0} boxes</div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, accent, testid }) {
  const cls = accent === "warning" ? "text-[color:var(--warning)]" : "text-[color:var(--gold)]";
  return (
    <div className="hos-card p-4" data-testid={testid}>
      <div className="flex items-center gap-2 label-caps">{icon} {label}</div>
      <div className={`font-heading text-3xl mt-1 ${cls}`}>{value ?? 0}</div>
    </div>
  );
}
